import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  visitReasonConfigSelector,
  reasonForVisitConfigSelector,
  visitorCompanyConfigSelector,
  contactInformationConfigSelector,
  floorConfigSelector,
  checkinInstructionsConfigSelector,
  acknowledgmentTextSelector,
  isVisitorsTypeaheadSupportedSelector,
  additionalNotificationRecipientsConfigSelector,
  noteForReceptionConfigSelector,
  visitorPhotoConfigSelector,
  notificationSelectionConfigSelector,
  passEntryOptionEnabledSelector,
  alternativeHostsEnabledSelector,
  hostInvitationCopyEnabledSelector,
  hostCheckinNotificationEnabledSelector,
  visitDurationConfigSelector,
  recurrenceEnabledSelector,
} from 'store/ui-metadata/selectors';
import { FeatureEnablementFlagEnum } from 'store/ui-metadata/types';
import { useFlag } from 'hooks/use-flag.hook';
import { LDFlags } from 'shared/consts';
import { useBuildingData } from 'store/building/hooks';

/**
 * Configuration for a single field
 */
export interface FieldConfig {
  hidden: boolean;
  required: boolean;
}

/**
 * Configuration for the notification selection section
 */
export interface NotificationFieldConfig {
  hidden: boolean;
  required: boolean;
  emailCheckbox: FieldConfig;
  smsCheckbox: FieldConfig;
}

/**
 * Configuration for all fields in the visit step
 */
export interface VisitStepFieldsConfig {
  visitSummary: FieldConfig;
  companyName: FieldConfig;
  email: FieldConfig;
  phone: FieldConfig;
  checkInType: FieldConfig;
  floor: FieldConfig;
  checkInInstructions: FieldConfig;
  visitorPhoto: FieldConfig;
  notificationSelection: NotificationFieldConfig;
  acknowledgmentText: string | undefined;
  typeaheadEnabled: boolean;
  alternativeHostsEnabled: boolean;
  additionalRecipients: FieldConfig;
  noteForReception: FieldConfig;
  passEntryOptionEnabled: boolean;
  hostInvitationCopyEnabled: boolean;
  hostCheckinNotificationEnabled: boolean;
  visitDuration: FieldConfig;
  recurrenceEnabled: boolean;
}

/**
 * Map FeatureEnablementFlagEnum to FieldConfig
 */
const mapFlagToConfig = (flag: FeatureEnablementFlagEnum): FieldConfig => ({
  hidden: flag === FeatureEnablementFlagEnum.DISABLED,
  required: flag === FeatureEnablementFlagEnum.ENABLED_REQUIRED,
});

/**
 * Hook to get field configuration from ui_metadata
 * Maps ui_metadata settings to field visibility and validation requirements
 */
export const useVisitStepFieldsConfig = (): VisitStepFieldsConfig => {
  const visitReasonFlag = useSelector(visitReasonConfigSelector);
  const reasonForVisitFlag = useSelector(reasonForVisitConfigSelector);
  const visitorCompanyFlag = useSelector(visitorCompanyConfigSelector);
  const contactInfoConfig = useSelector(contactInformationConfigSelector);
  const floorFlag = useSelector(floorConfigSelector);
  const checkinInstructionsFlag = useSelector(checkinInstructionsConfigSelector);
  const acknowledgmentText = useSelector(acknowledgmentTextSelector);
  const additionalNotificationRecipientsFlag = useSelector(additionalNotificationRecipientsConfigSelector);
  const noteForReceptionFlag = useSelector(noteForReceptionConfigSelector);
  const notificationSelectionFlag = useSelector(notificationSelectionConfigSelector);
  const showUiMetadata = useFlag(LDFlags.SHOW_VISIT_FORM_UI_METADATA, false);
  const typeaheadEnabled = useSelector(isVisitorsTypeaheadSupportedSelector);
  const alternativeHostsEnabled = useSelector(alternativeHostsEnabledSelector);
  const visitorPhotoFlag = useSelector(visitorPhotoConfigSelector);
  const passEntryOptionEnabled = useSelector(passEntryOptionEnabledSelector);
  const { passEntryOptions } = useBuildingData();
  const hasPassEntryOptions = (passEntryOptions?.length ?? 0) > 0;
  const hostInvitationCopyEnabled = useSelector(hostInvitationCopyEnabledSelector);
  const hostCheckinNotificationEnabled = useSelector(hostCheckinNotificationEnabledSelector);
  const visitDurationFlag = useSelector(visitDurationConfigSelector);
  const recurrenceEnabled = useSelector(recurrenceEnabledSelector);

  return useMemo(
    () => ({
      visitSummary: mapFlagToConfig(reasonForVisitFlag),
      companyName: mapFlagToConfig(visitorCompanyFlag),
      email: mapFlagToConfig(contactInfoConfig.email),
      phone: mapFlagToConfig(contactInfoConfig.phone),
      checkInType: showUiMetadata
        ? mapFlagToConfig(visitReasonFlag)
        : mapFlagToConfig(FeatureEnablementFlagEnum.DISABLED),
      floor: mapFlagToConfig(floorFlag),
      checkInInstructions: showUiMetadata
        ? mapFlagToConfig(checkinInstructionsFlag)
        : mapFlagToConfig(FeatureEnablementFlagEnum.DISABLED),
      visitorPhoto: mapFlagToConfig(visitorPhotoFlag),
      notificationSelection: {
        hidden:
          notificationSelectionFlag.email === FeatureEnablementFlagEnum.DISABLED &&
          notificationSelectionFlag.sms === FeatureEnablementFlagEnum.DISABLED,
        required:
          notificationSelectionFlag.email === FeatureEnablementFlagEnum.ENABLED_REQUIRED ||
          notificationSelectionFlag.sms === FeatureEnablementFlagEnum.ENABLED_REQUIRED,
        emailCheckbox: mapFlagToConfig(notificationSelectionFlag.email),
        smsCheckbox: mapFlagToConfig(notificationSelectionFlag.sms),
      },
      // Not gated by showUiMetadata: the BE rejects visit creation whenever the
      // building has acknowledgment_text configured and the payload omits it (VIS-1716).
      acknowledgmentText,
      typeaheadEnabled,
      alternativeHostsEnabled,
      additionalRecipients: mapFlagToConfig(additionalNotificationRecipientsFlag),
      noteForReception: showUiMetadata
        ? mapFlagToConfig(noteForReceptionFlag)
        : mapFlagToConfig(FeatureEnablementFlagEnum.DISABLED),
      passEntryOptionEnabled: showUiMetadata && passEntryOptionEnabled && hasPassEntryOptions,
      hostInvitationCopyEnabled,
      hostCheckinNotificationEnabled,
      visitDuration: mapFlagToConfig(visitDurationFlag),
      recurrenceEnabled,
    }),
    [
      visitReasonFlag,
      reasonForVisitFlag,
      visitorCompanyFlag,
      contactInfoConfig,
      floorFlag,
      checkinInstructionsFlag,
      visitorPhotoFlag,
      notificationSelectionFlag,
      acknowledgmentText,
      showUiMetadata,
      typeaheadEnabled,
      alternativeHostsEnabled,
      additionalNotificationRecipientsFlag,
      noteForReceptionFlag,
      passEntryOptionEnabled,
      hasPassEntryOptions,
      hostInvitationCopyEnabled,
      hostCheckinNotificationEnabled,
      visitDurationFlag,
      recurrenceEnabled,
    ],
  );
};
